import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';

const ConversationList = ({ 
  conversations, 
  loading, 
  error, 
  onSelect, 
  onStatusChange, 
  onDelete 
}) => {
  const [selectedConversations, setSelectedConversations] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Handle individual conversation selection
  const handleSelectConversation = (conversationId) => {
    const newSelected = new Set(selectedConversations);
    if (newSelected.has(conversationId)) {
      newSelected.delete(conversationId);
    } else {
      newSelected.add(conversationId);
    }
    setSelectedConversations(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  // Handle select all conversations
  const handleSelectAll = () => {
    if (selectedConversations.size === conversations.length) {
      setSelectedConversations(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedConversations(new Set(conversations.map(c => c.id)));
      setShowBulkActions(true);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async (newStatus) => {
    const conversationIds = Array.from(selectedConversations);
    
    // Apply status change to all selected conversations
    for (const id of conversationIds) {
      await onStatusChange(id, newStatus);
    }
    
    // Clear selection
    setSelectedConversations(new Set());
    setShowBulkActions(false);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedConversations.size} conversations? This action cannot be undone.`)) {
      return;
    }

    const conversationIds = Array.from(selectedConversations);

    // Delete all selected conversations
    for (const id of conversationIds) {
      await onDelete(id);
    }

    // Clear selection
    setSelectedConversations(new Set());
    setShowBulkActions(false);
  };

  // Handle individual delete
  const handleDeleteConversation = async (e, conversation) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete this conversation? This action cannot be undone.`)) {
      return;
    }

    await onDelete(conversation.id);
  };

  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Unknown';
    // Simple formatting for US numbers
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const number = cleaned.slice(1);
      return `+1 (${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
    return phoneNumber;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'active':
      case 'connected':
      case 'in-progress':
      case 'connecting':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'initiated':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Check if conversation can be deleted (completed or failed status)
  const canDeleteConversation = (status) => {
    return ['completed', 'failed', 'initiated'].includes(status);
  };

  // Check if conversation is unread (no last_viewed_at timestamp)
  const isConversationUnread = (conversation) => {
    return !conversation.last_viewed_at;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading conversations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-8 text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-8 text-center">
          <span className="text-4xl mb-4 block">💬</span>
          <p className="text-gray-600 dark:text-gray-400 mb-2 dark:text-gray-300 dark:text-gray-600">No conversations found</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Conversations will appear here once your AI agents start receiving calls.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="px-6 py-3 bg-teal-50 dark:bg-teal-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm text-teal-700 dark:text-teal-300">
              {selectedConversations.size} conversation{selectedConversations.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkStatusChange('completed')}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 dark:text-white"
              >
                Mark as Completed
              </button>
              <button
                onClick={() => handleBulkStatusChange('failed')}
                className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 dark:text-white"
              >
                Mark as Failed
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 text-xs bg-red-800 text-white rounded hover:bg-red-900 dark:text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Header */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedConversations.size === conversations.length && conversations.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>
          <div className="ml-6 grid grid-cols-7 gap-4 flex-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <div>Parrot</div>
            <div>Phone Number</div>
            <div>Caller Name</div>
            <div>Caller Phone</div>
            <div>Date</div>
            <div>Duration</div>
            <div>Status</div>
          </div>
          <div className="ml-6 w-32 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Actions
          </div>
        </div>
      </div>

      {/* Conversation Rows */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {conversations.map((conversation) => (
          <div
            key={conversation.id}
            className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer dark:bg-gray-900"
            onClick={() => onSelect(conversation)}
          >
            <div className="flex items-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedConversations.has(conversation.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectConversation(conversation.id);
                  }}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 dark:border-gray-600 rounded"
                />
              </div>
              <div className={`ml-6 grid grid-cols-7 gap-4 flex-1 text-sm ${isConversationUnread(conversation) ? 'font-bold' : ''}`}>
                <div className={`text-gray-900 dark:text-white ${isConversationUnread(conversation) ? 'font-bold' : 'font-medium'}`}>
                  {conversation.parrotName || 'Unknown Parrot'}
                </div>
                <div className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {formatPhoneNumber(conversation.phoneNumber)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {conversation.callerName || 'Unknown'}
                </div>
                <div className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {formatPhoneNumber(conversation.callerNumber)}
                </div>
                <div className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {conversation.startTime ? formatDistanceToNow(new Date(conversation.startTime), { addSuffix: true }) : 'Unknown'}
                </div>
                <div className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {formatDuration(conversation.duration)}
                </div>
                <div>
                  <span className={getStatusBadge(conversation.status)}>
                    {conversation.status}
                  </span>
                </div>
              </div>
              <div className="ml-6 w-32 flex space-x-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(conversation);
                  }}
                  className="text-teal-600 hover:text-teal-900 dark:text-teal-400 dark:hover:text-teal-300 text-sm font-medium"
                >
                  Details
                </button>
                {canDeleteConversation(conversation.status) && (
                  <button
                    onClick={(e) => handleDeleteConversation(e, conversation)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConversationList;
